import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { CACHE_STORES, CACHE_API_CONFIGS } from '../../constants/cache';
import { CacheConfig, CacheStoreConfig } from '@app/types/cache';

import { getCache, setCache, isCacheValid } from '../../utils/cache';

/**
 * Looks up the cache config for a given URL by matching only the path portion (ignores query string).
 *
 * Example:
 *   Pattern: new URLPattern('/foo/:id/bar')
 *   URL: '/foo/1234/bar?type=baz'
 *   This will match, because only the path is compared.
 *
 * If your pattern includes a query string (e.g., '?type=baz'), this will NOT match unless the query is stripped from both.
 * To support query-string-sensitive patterns, update this to match the full URL (path + query).
 */
export function getCacheConfig(url: string): CacheConfig | undefined {
  return CACHE_API_CONFIGS.find((config) => config.url.match(url.split('?')[0]));
}

/**
 * Builds a cache key from the request config.
 *
 * - If config.url is '/foo/1234/bar' and config.params = { type: 'baz' },
 *   the key will be '/foo/1234/bar?type=baz'.
 * - If config.params is not set but the query is present in config.url (e.g., '/foo/1234/bar?type=baz'),
 *   the key will be the full URL string.
 *
 * This ensures that requests with different query params gets different key.
 */
export function getCacheKey(config: InternalAxiosRequestConfig): string {
  let key = config.url ?? '';
  if (config.params) {
    const params =
      typeof config.params === 'string'
        ? config.params
        : Object.keys(config.params)
            .sort()
            .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(config.params[k])}`)
            .join('&');
    key += `?${params}`;
  }
  return key;
}

export function cacheInterceptor<T>(axiosInstance: AxiosInstance) {
  const storeConfig: CacheStoreConfig = CACHE_STORES.api;

  axiosInstance.interceptors.request.use(async (config) => {
    if (!config.url) return config;
    if ((config.method || 'get').toLowerCase() !== 'get') return config;

    const cacheConfig = getCacheConfig(config.url);
    if (!cacheConfig) return config;

    const cacheKey = getCacheKey(config);
    const cached = await getCache<T>(cacheKey, storeConfig);
    if (cached && isCacheValid(cached, cacheConfig)) {
      config.adapter = () =>
        Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (from cache)',
          headers: {
            'x-cache-source': 'local-cache',
          },
          config,
          request: {},
        });
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse<T>) => {
      if (
        response.config &&
        response.config.url &&
        (response.config.method || 'get').toLowerCase() === 'get' &&
        !(response.headers && response.headers['x-cache-source'] === 'local-cache')
      ) {
        const cacheConfig = getCacheConfig(response.config.url);
        if (cacheConfig) {
          const cacheKey = getCacheKey(response.config);
          await setCache<T>(cacheKey, response.data, storeConfig);
        }
      }
      return response;
    },
    (error) => Promise.reject(error),
  );
}
